"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";

import { DateTime } from "@/components/shared/date-time";
import { InitialsAvatar } from "@/components/shared/initials-avatar";
import { StatusBadge, type StatusTone } from "@/components/shared/status-badge";
import { cn } from "@/lib/utils";

import { useValueFormat } from "../../hooks/use-value-format";
import { useWidgetAction } from "../../hooks/use-widget-action";
import { WidgetEmpty } from "../widget-card";
import { isInAppPath } from "../../utils/href";

import type { ListItem, ListPayload, Tone } from "@/types";

// Widget tones -> the shared badge's tones. Backend tones are semantic
// ("negative"), never presentational, so this mapping is the only place the two
// vocabularies meet.
const TONE_MAP: Record<Tone, StatusTone> = {
  positive: "success",
  negative: "danger",
  neutral: "neutral",
};

/**
 * The feed renderer — one component behind every "last 5 X" widget.
 *
 * It knows nothing about notifications, users, orders or chat: rows are whatever
 * the backend put in the payload.
 */
export function ListWidget({ payload }: { payload: ListPayload }) {
  const t = useTranslations("Dashboard");

  if (payload.items.length === 0) {
    // The backend may explain WHY it's empty (e.g. a feature the user isn't set
    // up for), which reads better than a generic "no data".
    return <WidgetEmpty label={payload.empty_text ?? t("noData")} />;
  }

  return (
    // The DS's ListRow treatment: each row is its own raised tile on the card,
    // separated by a 6px gap instead of a hairline. There are no divider rules
    // anywhere in this design system — rows are told apart by elevation.
    <ul className="flex flex-col gap-1.5">
      {payload.items.map((item) => (
        <li
          key={item.id}
          className="rounded-md bg-surface-input px-3 shadow-raised"
        >
          <Row item={item} />
        </li>
      ))}
    </ul>
  );
}

function Row({ item }: { item: ListItem }) {
  const runAction = useWidgetAction();
  const format = useValueFormat();

  const content = (
    <div className="flex items-center gap-3 py-3">
      {(item.avatar_name || item.avatar_url) && (
        <InitialsAvatar
          name={item.avatar_name}
          image={item.avatar_url}
          size="sm"
          className="shrink-0"
        />
      )}

      {item.unread && (
        <span aria-hidden className="size-2 shrink-0 rounded-full bg-primary" />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            "truncate text-sm text-foreground",
            item.unread && "font-semibold",
          )}
        >
          {item.primary}
        </span>
        {item.secondary && (
          <span className="truncate text-xs text-fg-muted">
            {item.secondary}
          </span>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {item.badge && (
          // `value` is StatusBadge's lookup key for reference-data labels and
          // tone maps. We use neither — the backend already resolved both — so
          // it just mirrors the label.
          <StatusBadge
            value={item.badge.label}
            label={item.badge.label}
            tone={TONE_MAP[item.badge.tone]}
          />
        )}
        {item.timestamp && (
          // Rendered through the company-timezone layer, never formatted
          // backend-side.
          <DateTime value={item.timestamp} className="text-xs text-fg-muted" />
        )}

        {/*
          A figure at the row's trailing edge — a payslip's net pay today.
          Formatted here, not by the backend, so money lands in the company's
          own currency. Both halves are required: a value with no format has no
          renderable meaning, so it is not guesswork-rendered as a bare number.
        */}
        {item.value !== null && item.value_format && (
          <span className="text-sm font-semibold tabular-nums text-foreground">
            {format(item.value, item.value_format)}
          </span>
        )}
      </div>
    </div>
  );

  // An action instead of a path, for a row whose target has no URL — a chat room.
  // Checked first because a row is one or the other, and the backend is tested to
  // never send both.
  // Bound to a local so it stays narrowed inside the handler — TypeScript drops
  // property narrowing across a closure boundary.
  const action = item.action;

  if (action) {
    return (
      <button
        type="button"
        onClick={() => runAction(action, item.id)}
        // text-left because a button centres its content by default, which would
        // undo the row's own layout.
        className="block w-full text-left transition-opacity hover:opacity-70"
      >
        {content}
      </button>
    );
  }

  // Only a validated in-app path becomes a link — see utils/href.
  return isInAppPath(item.href) ? (
    <Link
      href={item.href}
      className="block transition-opacity hover:opacity-70"
    >
      {content}
    </Link>
  ) : (
    content
  );
}
